import { useLazyQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { Issue } from "generated-graphql/graphql";
import { useCallback, useRef, useState } from "react";

import { useApplyExtractedProfile, WASTE_PROFILE_INPUT_VALIDATOR } from "./api";
import { toApplyInput, toProfileInput } from "./extractionReview";

/**
 * Validating and applying a reviewed characterization.
 *
 * `validate` runs the same rules the apply itself enforces, as the reviewer
 * edits, so a contradiction shows up on the offending field rather than as a
 * rejected save. Issue keys already match the grid's, so nothing re-keys them.
 */
export function useApplyReviewedProfile({
  wasteStreamId,
  documentId,
  onApplied,
}: {
  wasteStreamId: string;
  documentId: string;
  onApplied: () => void;
}): {
  validate: (reviewed: ExtractionReviewValues) => Promise<Issue[]>;
  confirm: (reviewed: ExtractionReviewValues) => Promise<void>;
  applying: boolean;
  blockingIssues: string[];
} {
  const alerts = useAlerts();
  const [applyProfile, { loading: applying }] = useApplyExtractedProfile();
  const [runValidator] = useLazyQuery(WASTE_PROFILE_INPUT_VALIDATOR, {
    fetchPolicy: "no-cache",
  });
  const [blockingIssues, setBlockingIssues] = useState<string[]>([]);
  // Two callers run the validator — the grid on every edit, and `confirm` on
  // Apply — and nothing orders their responses. Only the newest may write, or an
  // earlier request resolving late clears the banner that is the live reason
  // Apply refused, leaving a button that did nothing and no explanation.
  const latestValidation = useRef(0);

  const validate = useCallback(
    async (reviewed: ExtractionReviewValues): Promise<Issue[]> => {
      const request = (latestValidation.current += 1);
      const result = await runValidator({
        variables: { input: toProfileInput(wasteStreamId, reviewed) },
      });
      if (result.error) throw result.error;
      const issues = result.data?.wasteProfileInputValidator ?? [];
      // The grid runs this as the reviewer edits, so it is also what tells us the
      // banner is out of date. Without this it keeps naming a problem they have
      // already fixed until they click Apply again — and the banner is the thing
      // explaining why Apply did nothing.
      if (request === latestValidation.current && issues.length === 0) {
        setBlockingIssues([]);
      }
      return issues;
    },
    [runValidator, wasteStreamId]
  );

  const confirm = useCallback(
    async (reviewed: ExtractionReviewValues) => {
      // Re-validated here, deliberately, and not left to the grid's inline pass.
      // Neither layer below is a gate: the rules are ERROR-level, so
      // `applyExtractedProfile` accepts a characterization that disagrees with
      // itself, and `useValidatingForm` — which the grid runs on — passes an
      // `onInvalid` handler that calls the submit handler anyway, so a form it
      // has marked invalid still commits. Surfacing an issue and refusing to act
      // on it are separate jobs there, and this is where the second one happens,
      // the same way the Project 2 edit dialog does it before saving.
      let issues: Issue[];
      try {
        issues = await validate(reviewed);
      } catch (error) {
        // A validator that failed to answer is not a validator that approved.
        alerts.error(
          "Could not validate the characterization",
          error instanceof Error ? error.message : undefined
        );
        return;
      }
      if (issues.length > 0) {
        setBlockingIssues(issues.map((issue) => issue.message));
        return;
      }
      // Unconditionally, unlike the sequence-guarded clear inside `validate`. A
      // newer validation still in flight is a reason to distrust *its* result,
      // not the one this decision was just made on — and if the mutation below
      // fails, the dialog stays open with the banner still naming a problem the
      // reviewer has already fixed.
      setBlockingIssues([]);

      try {
        await applyProfile({
          variables: { input: toApplyInput(wasteStreamId, documentId, reviewed) },
        });
        alerts.success("Characterization applied to the profile");
        onApplied();
      } catch (error) {
        alerts.error(
          "Could not apply the characterization",
          error instanceof Error ? error.message : undefined
        );
      }
    },
    [alerts, applyProfile, documentId, onApplied, validate, wasteStreamId]
  );

  return { validate, confirm, applying, blockingIssues };
}
